import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {
  BenchlingExpression,
  BenchlingExpressionConstantValue,
  BenchlingExpressionFunctionParameter,
  BenchlingExpressionSchemaLookup,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ComplexTypeOperator} from 'data-types/benchling-type-def/ComplexTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import {LookupDropdownOptionsQuery} from 'processes/modals/router-config/__generated/LookupDropdownOptionsQuery';
import {
  sharedStyles,
  ConditionalLogicAllowedValuesBaseProps,
} from 'processes/modals/router-config/RouterConfigConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import MultiSelectSearchableDropdown from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import PlaceholderText from 'ui-library/PlaceholderText';
import {DeepPartial} from 'util/Types';

import {SchemaFieldSelectorOptionGraphQL} from '../../../../graphql-fragments/schemas/__generated/SchemaFieldSelectorOptionGraphQL';

export const LOOKUP_DROPDOWN_OPTIONS_QUERY = gql`
  query LookupDropdownOptionsQuery($id: StrictString!) @skipAudit {
    schemaFieldSelector(id: $id) {
      ... on SchemaFieldSelectorNode {
        id
        schemaFieldSelectorOptions {
          ...SchemaFieldSelectorOptionGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelectorOption').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const SINGLE_VALUE_PLACEHOLDER_TEXT = 'select value';
const MULTI_VALUE_PLACEHOLDER_TEXT = 'select value(s)';

export interface ConditionalLogicAllowedValuesProps extends ConditionalLogicAllowedValuesBaseProps {
  isLookupValueMultiplex: boolean;
  selectorId: string | null;
  dataTestAttrs?: DataTestAttrs;
}

interface LogicValueDropdownProps extends ConditionalLogicAllowedValuesBaseProps {
  disabled: boolean;
  options: Array<SchemaFieldSelectorOptionGraphQL>;
  placeholder: React.ReactNode;
  selectorId: string | null;
  lookupParameter?: DeepPartial<BenchlingExpressionSchemaLookup> | null;
}

export const INVALID_TYPE_ERROR_MSG =
  'This value is not compatible with the selected function. Please choose a new function or reconfigure the left hand lookup.';

function SingleValueDropdown(props: LogicValueDropdownProps): JSX.Element {
  const valueId = props.constantExpression ? props.constantExpression.value : null;
  const typeDef = props.constantExpression?.itemType?.name ?? null;
  const errorText = typeDef == null || typeDef === LeafType.DROPDOWN_OPTION ? null : INVALID_TYPE_ERROR_MSG;

  const valueName = props.options.find((option) => option.id === valueId)?.name ?? props.placeholder;
  const optionDatas = props.options.map((option) => ({name: option.name, value: option.id}));
  const optionsById = _.indexBy(props.options, 'id');
  const selectedValueId = optionsById[String(valueId)] ? optionsById[String(valueId)].id : null;
  return (
    <SelectDropdown
      disabled={errorText != null || props.disabled}
      error={errorText}
      errorStyle={SelectDropdownErrorStyle.TOOLTIP}
      className={css(sharedStyles.routerInputControl)}
      displayName={valueName}
      shouldEllipsize={true}
      menu={
        <SearchableMenu
          height={MenuHeight.MEDIUM}
          width={MenuWidth.FULL}
          scrollable={true}
          selectedValue={selectedValueId}
          optionDatas={optionDatas}
          onSelect={(selectedValue) => {
            const newParam = {
              name: props.expression?.parameters?.[1]?.name,
              expression: {
                type: BenchlingExpressionType.CONSTANT_VALUE,
                itemType: {
                  name: LeafType.DROPDOWN_OPTION,
                  selectorId: props.selectorId,
                },
                value: optionsById[selectedValue].id,
              },
            };
            const newExpression = {
              ...props.expression,
              parameters: [props.lookupParameter ?? {}, newParam],
            };

            props.setExpression(newExpression);
          }}
        />
      }
      dataTestAttrs={{component: 'ConditionalLogicValueDropdown', element: 'singleValueDropdown'}}
    />
  );
}

function isMultiplexLHSValid(
  constantExpression: DeepPartial<BenchlingExpressionConstantValue> | null
): boolean {
  const typeDef = constantExpression?.itemType?.name ?? null;
  return typeDef == null || typeDef === ComplexTypeOperator.LIST;
}

function MultiValueDropdown(props: LogicValueDropdownProps): JSX.Element {
  const valueIds = props.constantExpression ? [props.constantExpression.value].flat() : [];
  const errorText = isMultiplexLHSValid(props.constantExpression) ? null : INVALID_TYPE_ERROR_MSG;

  const optionDatas = props.options.map((option) => ({name: option.name, value: option.id}));
  const optionsById = _.indexBy(props.options, 'id');
  const selectedValueIds = valueIds
    .map((optionId) => (optionsById[String(optionId)] ? optionsById[String(optionId)].id : null))
    .filter((id): id is string => id != null);
  return (
    <MultiSelectSearchableDropdown
      disabled={props.disabled || errorText != null}
      error={errorText}
      errorStyle={SelectDropdownErrorStyle.TOOLTIP}
      className={css(sharedStyles.routerInputControl)}
      noneSelectedMessage={props.placeholder}
      selectedValues={selectedValueIds}
      optionDatas={optionDatas}
      onSelect={(selectedValues) => {
        const newParam: BenchlingExpressionFunctionParameter = {
          name: props.expression?.parameters?.[1]?.name ?? '',
          expression: {
            type: BenchlingExpressionType.CONSTANT_VALUE,
            itemType: {
              name: ComplexTypeOperator.LIST as ComplexTypeOperator.LIST,
              ofType: LeafType.DROPDOWN_OPTION,
              selectorId: props.selectorId,
            },
            value: selectedValues.map((selectedId) => optionsById[selectedId].id),
          },
        };
        const newExpression: DeepPartial<BenchlingExpression> = {
          ...props.expression,
          parameters: [props.lookupParameter ?? {}, newParam],
        };

        props.setExpression(newExpression);
      }}
      dataTestAttrs={{component: 'ConditionalLogicValueDropdown', element: 'multiValueDropdown'}}
    />
  );
}

export default function ConditionalLogicValueDropdown(
  props: ConditionalLogicAllowedValuesProps
): JSX.Element | null {
  const result = useSkippableBenchlingQuery<LookupDropdownOptionsQuery>(LOOKUP_DROPDOWN_OPTIONS_QUERY, {
    skip: props.selectorId == null,
    variables: {id: props.selectorId},
  });
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load possible values; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    let options: Array<SchemaFieldSelectorOptionGraphQL> = [];
    if (result.type !== QueryResultType.SKIPPED) {
      if (result.data.schemaFieldSelector.__typename === 'InaccessibleInfoNode') {
        notifications.error('Lookup value was inaccessible.');
        return null;
      }
      options = result.data.schemaFieldSelector.schemaFieldSelectorOptions;
    }

    const placeholder = (
      <PlaceholderText
        value={props.isLookupValueMultiplex ? MULTI_VALUE_PLACEHOLDER_TEXT : SINGLE_VALUE_PLACEHOLDER_TEXT}
      />
    );

    if (props.isLookupValueMultiplex) {
      return (
        <MultiValueDropdown
          disabled={props.selectorId == null}
          constantExpression={props.constantExpression}
          expression={props.expression}
          setExpression={props.setExpression}
          options={options}
          placeholder={placeholder}
          selectorId={props.selectorId}
          lookupParameter={props.expression?.parameters?.[0]}
        />
      );
    } else {
      return (
        <SingleValueDropdown
          disabled={props.selectorId == null}
          constantExpression={props.constantExpression}
          expression={props.expression}
          setExpression={props.setExpression}
          options={options}
          placeholder={placeholder}
          selectorId={props.selectorId}
          lookupParameter={props.expression?.parameters?.[0]}
        />
      );
    }
  }
}
